import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";
import { Sparkles, Loader2, Send, TrendingUp, TrendingDown, Scale, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import ChartBox from "@/components/ChartBox";
import ExportButtons from "@/components/ExportButtons";

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-xl">
      <div className="mb-1 font-semibold">{label}</div>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2 font-mono">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span>{money(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export default function Reports() {
  const [data, setData] = useState(null);
  const [vat, setVat] = useState(null);
  const [ai, setAi] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState("");
  const [rStart, setRStart] = useState("");
  const [rEnd, setREnd] = useState("");
  const [d300Period, setD300Period] = useState("");

  const doExport = async (format) => {
    setExporting(format);
    try {
      const p = { format };
      if (rStart) p.start = rStart;
      if (rEnd) p.end = rEnd;
      await api.downloadExport(`/export/report?${new URLSearchParams(p).toString()}`, `finakis_rapor.${format}`);
      toast.success(`Rapor ${format.toUpperCase()} olarak indirildi`);
    } catch {
      toast.error("Dışa aktarma başarısız");
    } finally {
      setExporting("");
    }
  };

  const downloadD300 = async () => {
    if (!d300Period) {
      toast.error("Lütfen bir dönem seçin");
      return;
    }
    try {
      await api.downloadExport(`/export/vat-d300?period=${d300Period}`, `D300_${d300Period}.pdf`);
      toast.success("D300 beyannamesi indirildi");
    } catch {
      toast.error("D300 oluşturulamadı");
    }
  };

  useEffect(() => {
    api.summary().then(setData);
    api.vatReport().then(setVat).catch(() => {});
  }, []);

  const ask = async (question) => {
    setAiLoading(true);
    setAi("");
    try {
      const res = await api.aiInsights(question);
      setAi(res.insight);
    } catch {
      toast.error("AI analizi başarısız oldu");
    } finally {
      setAiLoading(false);
    }
  };

  if (!data) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const totalInc = data.cashflow.reduce((s, c) => s + c.gelir, 0);
  const totalExp = data.cashflow.reduce((s, c) => s + c.gider, 0);

  const suggestions = [
    "Giderlerimi nasıl azaltabilirim?",
    "Nakit akışım sağlıklı mı?",
    "Önümüzdeki ay için tahmin yap",
  ];

  return (
    <div className="p-5 pt-20 lg:p-10 lg:pt-10">
      <PageHeader
        title="Finansal Raporlar"
        subtitle="Son 6 ay performans analizi ve AI öngörüleri"
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Input data-testid="report-start-date" type="date" value={rStart} onChange={(e) => setRStart(e.target.value)} title="Başlangıç" className="h-9 w-[140px]" />
            <span className="text-muted-foreground">–</span>
            <Input data-testid="report-end-date" type="date" value={rEnd} onChange={(e) => setREnd(e.target.value)} title="Bitiş" className="h-9 w-[140px]" />
            <Button data-testid="report-export-xlsx" size="sm" variant="outline" disabled={exporting === "xlsx"} onClick={() => doExport("xlsx")} className="gap-1.5"><FileSpreadsheet className="h-4 w-4 text-emerald-400" /> Excel</Button>
            <Button data-testid="report-export-pdf" size="sm" variant="outline" disabled={exporting === "pdf"} onClick={() => doExport("pdf")} className="gap-1.5"><FileText className="h-4 w-4 text-rose-400" /> PDF</Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><TrendingUp className="h-4 w-4 text-emerald-400" /> 6 Aylık Gelir</div>
          <div className="mt-3 font-mono text-2xl font-semibold tabular text-emerald-400">{money(totalInc)}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><TrendingDown className="h-4 w-4 text-rose-400" /> 6 Aylık Gider</div>
          <div className="mt-3 font-mono text-2xl font-semibold tabular text-rose-400">{money(totalExp)}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Scale className="h-4 w-4 text-primary" /> Net Sonuç</div>
          <div className={`mt-3 font-mono text-2xl font-semibold tabular ${totalInc - totalExp >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{money(totalInc - totalExp)}</div>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-border bg-card p-6">
        <h3 className="mb-6 font-display text-lg font-medium">Net Nakit Akışı Trendi</h3>
        <ChartBox height={300}>
          {(w, h) => (
          <BarChart width={w} height={h} data={data.cashflow} margin={{ left: -10, right: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
            <XAxis dataKey="month" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
            <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1000}k`} />
            <Tooltip content={<ChartTooltip />} cursor={{ fill: "#ffffff08" }} />
            <Bar dataKey="net" radius={[4, 4, 0, 0]} maxBarSize={48} isAnimationActive={false}>
              {data.cashflow.map((c, i) => (
                <Cell key={i} fill={c.net >= 0 ? "#10B981" : "#F43F5E"} />
              ))}
            </Bar>
          </BarChart>
          )}
        </ChartBox>
      </div>

      {/* Aylık KDV Raporu */}
      <div className="mt-4 rounded-xl border border-border bg-card p-6" data-testid="vat-report-card">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-display text-lg font-medium">Aylık KDV Raporu</h3>
            <p className="text-xs text-muted-foreground">Faturalardan otomatik hesaplanan aylık KDV özeti</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              data-testid="d300-period-select"
              value={d300Period}
              onChange={(e) => setD300Period(e.target.value)}
              className="h-9 rounded-lg border border-border bg-background px-3 text-sm"
            >
              <option value="">D300 dönemi seç...</option>
              {vat?.rows?.map((r) => (
                <option key={r.ym} value={r.ym}>{r.month_label}</option>
              ))}
            </select>
            <Button data-testid="d300-download" size="sm" variant="outline" onClick={downloadD300} className="gap-1.5">
              <FileText className="h-4 w-4 text-teal-400" /> D300 Beyanname
            </Button>
            <div className="mx-1 hidden h-5 w-px bg-border sm:block" />
            <ExportButtons path="/export/vat" label="KDV raporu" testidPrefix="vat-export" />
          </div>
        </div>

        {!vat ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Yükleniyor...</div>
        ) : vat.rows.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Fatura verisi bulunamadı</div>
        ) : (
          <>
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-lg border border-border/60 bg-secondary/20 p-4">
                <div className="text-xs text-muted-foreground">Toplam Hesaplanan KDV</div>
                <div className="mt-1 font-mono text-xl font-semibold text-emerald-400">{money(vat.totals.output_vat)}</div>
              </div>
              <div className="rounded-lg border border-border/60 bg-secondary/20 p-4">
                <div className="text-xs text-muted-foreground">Toplam İndirilecek KDV</div>
                <div className="mt-1 font-mono text-xl font-semibold text-blue-400">{money(vat.totals.input_vat)}</div>
              </div>
              <div className="rounded-lg border border-border/60 bg-secondary/20 p-4">
                <div className="text-xs text-muted-foreground">Net Ödenecek KDV</div>
                <div className={`mt-1 font-mono text-xl font-semibold ${vat.totals.net_vat >= 0 ? "text-amber-400" : "text-emerald-400"}`}>{money(vat.totals.net_vat)}</div>
              </div>
            </div>
            <div className="max-h-[360px] overflow-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card">
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-3 font-semibold">Dönem</th>
                    <th className="px-4 py-3 text-right font-semibold">Hesaplanan KDV</th>
                    <th className="px-4 py-3 text-right font-semibold">İndirilecek KDV</th>
                    <th className="px-4 py-3 text-right font-semibold">Ödenecek KDV</th>
                    <th className="px-4 py-3 text-right font-semibold">Fatura</th>
                  </tr>
                </thead>
                <tbody>
                  {vat.rows.map((r) => (
                    <tr key={r.ym} data-testid="vat-row" className="border-b border-border/50 hover:bg-secondary/30">
                      <td className="px-4 py-2.5 font-medium">{r.month_label}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-emerald-400">{money(r.output_vat)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-blue-400">{money(r.input_vat)}</td>
                      <td className={`px-4 py-2.5 text-right font-mono font-semibold ${r.net_vat >= 0 ? "text-amber-400" : "text-emerald-400"}`}>{money(r.net_vat)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-muted-foreground">{r.invoice_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="ai-glow mt-4 overflow-hidden rounded-xl border border-amber-500/30 bg-card p-6">
        <div className="mb-4 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/15 text-amber-400"><Sparkles className="h-[18px] w-[18px]" /></div>
          <div>
            <h3 className="font-display text-lg font-medium">AI Rapor Danışmanı</h3>
            <p className="text-xs text-muted-foreground">Finansal verilerinize soru sorun</p>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {suggestions.map((s) => (
            <button key={s} data-testid="ai-suggestion" onClick={() => { setQ(s); ask(s); }} disabled={aiLoading} className="rounded-full border border-amber-500/30 bg-amber-500/5 px-3 py-1.5 text-xs text-amber-300 transition-colors hover:bg-amber-500/15 disabled:opacity-50">{s}</button>
          ))}
        </div>

        <div className="flex gap-2">
          <Input
            data-testid="ai-question-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && q && ask(q)}
            placeholder="Örn: En büyük gider kalemim ne?"
            className="bg-background"
          />
          <Button data-testid="ai-ask-button" onClick={() => q && ask(q)} disabled={aiLoading || !q} className="bg-amber-500 text-black hover:bg-amber-400">
            {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>

        <div data-testid="ai-report-content" className="mt-4 min-h-[80px] text-sm leading-relaxed text-slate-300">
          {aiLoading && !ai && <span className="text-muted-foreground">Analiz ediliyor...</span>}
          {!aiLoading && !ai && <span className="text-muted-foreground">Yukarıdan bir soru seçin veya kendi sorunuzu yazın.</span>}
          {ai && <div className="whitespace-pre-wrap">{ai}</div>}
        </div>
      </div>
    </div>
  );
}
